import React, { useState } from 'react';
import { HelpCircle, ChevronUp, ChevronDown } from 'lucide-react';
import { Link, router } from '@inertiajs/react';
import { SettingsLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  Button,
  Badge,
  Card,
  CardBody,
  Toggle,
  ConfirmDialog,
} from '../../../design-system/components';
import {
  admin_settings_application_question_path,
  new_admin_settings_application_question_path,
  edit_admin_settings_application_question_path,
  toggle_active_admin_settings_application_question_path,
  move_admin_settings_application_question_path,
} from '../../../lib/routes';
export default function ApplicationQuestions({ questions }) {
  const [deleteTarget, setDeleteTarget] = useState(null);
  function handleMove(question, direction) {
    router.post(
      move_admin_settings_application_question_path(question.id),
      {
        direction,
      },
      {
        preserveScroll: true,
      },
    );
  }
  function handleToggleActive(question) {
    router.post(
      toggle_active_admin_settings_application_question_path(question.id),
      {},
      {
        preserveScroll: true,
      },
    );
  }
  function handleDelete(question) {
    router.delete(admin_settings_application_question_path(question.id), {
      onSuccess: () => setDeleteTarget(null),
    });
  }
  return (
    <SettingsLayout>
      <PageHeader
        title="Application Questions"
        description="Manage questions shown on the affiliate application form"
        actions={
          <Link href={new_admin_settings_application_question_path()}>
            <Button>Add Question</Button>
          </Link>
        }
      />

      <div className="mt-6 space-y-3">
        {questions.length === 0 ? (
          <Card>
            <CardBody>
              <div className="py-8 text-center">
                <HelpCircle size={48} className="mx-auto text-neutral-300" />
                <h3 className="mt-4 text-sm font-medium text-fg">No questions yet</h3>
                <p className="mt-1 text-sm text-fg-tertiary">
                  Add questions that applicants must answer when applying.
                </p>
                <div className="mt-4">
                  <Link href={new_admin_settings_application_question_path()}>
                    <Button>Add Question</Button>
                  </Link>
                </div>
              </div>
            </CardBody>
          </Card>
        ) : (
          questions.map((question, index) => (
            <Card key={question.id}>
              <CardBody>
                <div className="flex items-start gap-4">
                  {/* Reorder Buttons */}
                  <div className="flex flex-col gap-1 pt-0.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMove(question, 'up')}
                      disabled={index === 0}
                      className="!p-0.5"
                      title="Move up"
                    >
                      <ChevronUp size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMove(question, 'down')}
                      disabled={index === questions.length - 1}
                      className="!p-0.5"
                      title="Move down"
                    >
                      <ChevronDown size={16} />
                    </Button>
                  </div>

                  {/* Question Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-fg">
                        {question.question_text}
                      </span>
                      {question.required && (
                        <Badge variant="info" size="sm">
                          Required
                        </Badge>
                      )}
                      {!question.active && (
                        <Badge variant="default" size="sm">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-fg-tertiary">Position {question.position}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <Toggle
                      checked={question.active}
                      onChange={() => handleToggleActive(question)}
                      label=""
                    />
                    <Link
                      href={edit_admin_settings_application_question_path(question.id)}
                      className="text-sm text-fg hover:text-fg-secondary"
                    >
                      Edit
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(question)}
                      className="text-danger-600 hover:text-danger-700"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        title="Delete Question"
        message={`Are you sure you want to delete "${deleteTarget?.question_text}"? This cannot be undone if there are no existing answers.`}
        confirmLabel="Delete"
        destructive
      />
    </SettingsLayout>
  );
}
